/**
 * Contact Teachability Service
 * Handles API operations for lecturer teachability data
 */

import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Interface for contact teachability record
 */
export interface ContactTeachability {
  contact_teachability_id: number;
  fk_school_id: number;
  school_shortname: string | null;
  school_name?: string | null;
  module_code: string | null;
  module_name: string | null;
  teachability_code: string | null;
  lecture_wage: number | null;
  billed_party: boolean | null;
  planning_q1: boolean | null;
  planning_q2: boolean | null;
  planning_q3: boolean | null;
  planning_q4: boolean | null;
  opt_out: boolean;
  pdf_url?: string | null;
}

/**
 * Interface for teacher manager record
 */
export interface TeacherManager {
  fk_teacher_id: number;
  fk_school_id?: number;
  school: string;
  school_name?: string;
  manager: string;
}

@Injectable({
  providedIn: 'root',
})
export class ContactTeachabilityService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api.apiUrl}/api/lecturer/tp_teachabilities`;

  // Signals for reactive state management
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly teachabilities = signal<ContactTeachability[]>([]);
  readonly managers = signal<TeacherManager[]>([]);

  /**
   * Get all teachabilities for a specific lecturer
   *
   * @param contactId The lecturer's contact ID
   */
  getTeachabilities(contactId: number): Observable<ContactTeachability[]> {
    this.loading.set(true);
    this.error.set(null);

    return this.http.get<ContactTeachability[]>(`${this.baseUrl}/contact/${contactId}`).pipe(
      tap((data) => {
        this.teachabilities.set(data);
        this.loading.set(false);
      }),
      catchError((err) => {
        const errorMessage = err.error?.detail || err.message || 'Failed to load teachabilities';
        this.error.set(errorMessage);
        this.loading.set(false);
        console.error('❌ Error loading teachabilities:', err);
        return throwError(() => new Error(errorMessage));
      }),
    );
  }

  /**
   * Update teachability opt_out status
   *
   * @param teachabilityId The teachability ID
   * @param optOut The boolean value
   */
  updateOptOut(teachabilityId: number, optOut: boolean): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/${teachabilityId}`, { opt_out: optOut }).pipe(
      tap((updated) => {
        this.teachabilities.update((items) =>
          items.map((item) =>
            item.contact_teachability_id === teachabilityId
              ? { ...item, opt_out: optOut }
              : item,
          ),
        );
      }),
      catchError((err) => {
        console.error('❌ Error updating opt_out status:', err);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Get managers for a specific teacher
   *
   * @param teacherId The teacher's contact ID
   */
  getTeacherManagers(teacherId: number): Observable<TeacherManager[]> {
    return this.http.get<TeacherManager[]>(`${this.baseUrl}/p_teacher_managers/${teacherId}`).pipe(
      tap((data) => {
        this.managers.set(data);
      }),
      catchError((err) => {
        console.error('❌ Error loading teacher managers:', err);
        return throwError(() => new Error('Failed to load teacher managers'));
      }),
    );
  }

  /**
   * Get all modulplansecure links for a teacher
   *
   * @param contactId The teacher's contact ID
   */
  getModulplans(contactId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/contact/${contactId}/modulplans`).pipe(
      catchError((err) => {
        console.error('❌ Error loading modulplans:', err);
        return throwError(() => new Error('Failed to load modulplans'));
      }),
    );
  }

  /**
   * Clear all state
   */
  clear(): void {
    this.teachabilities.set([]);
    this.managers.set([]);
    this.loading.set(false);
    this.error.set(null);
  }
}
